import { all, call, debounce, put, retry, takeEvery, takeLatest } from 'redux-saga/effects';
import moment from 'moment';
import { select } from 'redux-saga/effects';
import ACTIONS from '../app.constants';
import { licenseActions, siteActions } from '../actions';
import { API_CONFIGURATIONS, Api } from '../services/services';
import { getAccessToken } from './authentication.sagas';
import {
    ApplicationActionType,
    DeleteInspectionActionType,
    LicenseSearchActionType,
    LicenseUpdateActionType,
    AuditEventActionType,
    Application,
    LicenseListActionType,
    LicenseListFiltersType,
    LicenseTypeConfiguration,
    Form,
    AddViolationType,
    Violation,
    Inspection,
    AddInspectionActionType,
    DeleteViolationActionType,
    AddLicenseActionType,
    License,
    MergeTemplate,
    DownloadTemplateDocumentActionType,
    PrintDocumentHistoryListActionType,
    ViolationList,
    CommentList,
    InspectionType,
    AccountingTransactionList,
    LicenseList,
    AuditHistoryList,
    LicenseTypeListType,
    ViolationCategory,
    Comment,
    User,
    DeleteLicenseFileActionType,
} from '../modules/application/types';

import { doubleDecodeURL } from '../../utils/utils';
import defaultEditForm from '../../views/LicenseDetailTabs/Tabs/LicenseData/defaultEditForm.json';
import { editFormData } from '../../utils/dummyData';
import { BASE_URL } from '../../routes';
import { Person } from '../modules/person/types';
import { LimitAndSkipData, MergeQueueHistory } from '../modules/calendar/types';
import { getInspectionCount } from '../../store/selectors/license.selectors';

function redirectToURL(history: any, location: string) {
    history.push(location);
}

function* getApplicationList(action: LicenseListActionType) {
    try {
        yield put(licenseActions.getApplicationList.pending(action.data));
        const token: string = yield call(getAccessToken, 'licenses.read');
        let licensesCount: number = 0;

        if (
            action.data?.pageNumber === 0 &&
            (action.payload?.data?.licenseStatus?.length ||
                action.payload?.data?.licenseType?.length ||
                action.payload?.data?.fromDate ||
                action.payload?.data?.toDate)
        ) {
            const countFiltered: number = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getApplicationCount,
                token,
                action.payload.data,
            );

            licensesCount = countFiltered;
        }

        const response: LicenseList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getApplicationList,
            token,
            undefined,
            (action.payload && action.payload.data) || undefined,
            action.data,
        );

        yield put(licenseActions.getApplicationList.success(response, action.data.pageNumber, licensesCount));
        if (action.data.done) action.data.done();
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getApplicationList.error(error));
        if (action.data.done) action.data.done();
    }
}

function* getApplication(action: ApplicationActionType) {
    const id: string = action.payload.id;
    let licenseType: LicenseTypeConfiguration;
    let license: Application;
    let editForm: Form = defaultEditForm;
    try {
        yield put(licenseActions.getApplicationDetail.pending);
        const licensesToken: string = yield call(getAccessToken, 'licenses.read');
        license = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getApplication,
            licensesToken,
            id,
        );
        const licenseTypesToken: string = yield call(getAccessToken, 'licenseTypes.read');
        licenseType = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getLicenseType,
            licenseTypesToken,
            license.type.id,
        );
        if (licenseType?.id) {
            try {
                if (licenseType?.views?.edit?.formId) {
                    const formsToken: string = yield call(getAccessToken, 'forms.read');
                    editForm = yield retry(
                        API_CONFIGURATIONS.MAX_RETRY_COUNT,
                        API_CONFIGURATIONS.RETRY_INTERVAL,
                        Api.getForm,
                        formsToken,
                        licenseType.views.edit.formId,
                    );
                }
            } catch (error) {
                yield put(
                    siteActions.setError({
                        isError: false,
                        errorCode: 0,
                        message: 'Something went wrong. Please contact IT support.',
                    }),
                );
            }
        }
        yield put(licenseActions.getApplicationDetail.success(license, licenseType, editForm));
    } catch (error) {
        // TODO
        yield put(siteActions.setError(error));
        yield put(licenseActions.getApplicationDetail.error(error));
    }
}

function* deleteLicenseFile(action: DeleteLicenseFileActionType) {
    if (!action.error) {
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'File has been removed.' }));
    } else {
        yield put(siteActions.setError({ isError: true, errorCode: 1, message: 'File failed to be removed.' }));
    }
}

function* getLicenseAccounting(action: ApplicationActionType) {
    const id: string = action.payload.id;
    try {
        yield put(licenseActions.getLicenseAccounting.pending);

        const token: string = yield call(getAccessToken, 'licenses.read');

        let response: AccountingTransactionList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getLicenseAccounting,
            token,
            id,
            action.paginationData,
        );
        response = response.filter((transaction) => transaction.journalEntry.invoicing?.status !== 'Void');

        yield put(licenseActions.getLicenseAccounting.success(response, action.paginationData));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);

        yield put(siteActions.setError(error));
        yield put(licenseActions.getLicenseAccounting.error(error));
    }
}

function* getAuditHistoryList(action: AuditEventActionType) {
    const id: string = action.payload.id;
    const entityType: string = action.payload.entityType;
    const paginationData: LimitAndSkipData = action.payload.paginationData;
    try {
        yield put(licenseActions.getAuditHistory.pending);
        const token: string = yield call(getAccessToken, 'licenses.read');
        const response: AuditHistoryList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,

            Api.getAuditHistoryList,
            token,
            id,
            entityType,
            paginationData,
        );
        yield put(licenseActions.getAuditHistory.success(response.length ? response : [], paginationData));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getAuditHistory.error(error));
    }
}

function* licenseSearch(action: LicenseSearchActionType) {
    const data: string = action.payload.data;

    try {
        yield put(licenseActions.licenseSearch.pending(data));

        const token: string = yield call(getAccessToken, 'licenses.read');

        const personToken: string = yield call(getAccessToken, 'persons.read');

        const response: Application[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getApplicationList,
            token,
            data,
            undefined,
            {
                pageNumber: 0,
                numberOfRows: 100,
            },
        );

        yield put(licenseActions.personSearch.pending(data));

        const persons: Person[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPersonListSearched,
            personToken,
            data,
            {
                pageNumber: 0,
                numberOfRows: 100,
            },
        );

        yield put(licenseActions.personSearch.success(persons));

        yield put(licenseActions.licenseSearch.success(response));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.licenseSearch.error(error));
    }
}

function* updateLicense(action: LicenseUpdateActionType) {
    const data: Application = action.payload.data;
    const licenseId: string = action.payload.licenseId;
    try {
        yield put(licenseActions.updateLicense.pending());
        const token: string = yield call(getAccessToken, 'licenses.read');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.updateLicense,
            token,
            data,
            licenseId,
        );
        yield put(licenseActions.getApplicationDetailBegin(licenseId));
        yield put(licenseActions.updateLicense.success());
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Information has been saved.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.updateLicense.error(error));
    }
}

function* applyFilter(action: LicenseListActionType) {
    yield getApplicationList(action);
}

function* exportLicenses(action: LicenseListActionType) {
    const data: LicenseListFiltersType = action.payload.data;

    try {
        const token: string = yield call(getAccessToken, 'licenses.read');
        const csvContent: string = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.exportLicenses,
            token,
            data,
        );
        if (!!csvContent) {
            const date = new Date().toUTCString();
            const newDate = moment(date).format('MM-DD-YYYY');
            const download = document.createElement('a');
            download.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
            download.setAttribute('download', `Report_${newDate}.csv`);
            download.click();
        } else {
            yield put(siteActions.setError({ isError: true, errorCode: 0, message: 'No data Found.' }));
        }
    } catch (error) {
        yield put(siteActions.setError(error));
    }
}

function* getLicenseTypes() {
    try {
        yield put(licenseActions.getLicenseTypes.pending);
        const token: string = yield call(getAccessToken, 'licenseTypes.read');
        let response: LicenseTypeListType = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getLicenseTypes,
            token,
        );
        yield put(licenseActions.getLicenseTypes.success(response));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getLicenseTypes.error(error));
    }
}

function* getViolationList(action: ApplicationActionType) {
    const licenseId: string = action.payload.id;
    try {
        yield put(licenseActions.getViolationList.pending);
        const token: string = yield call(getAccessToken, 'violations.read');
        const response: ViolationList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getViolations,
            token,
            licenseId,
            action?.paginationData,
        );
        yield put(licenseActions.getViolationList.success(response, action.paginationData));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getViolationList.error(error));
    }
}

function* getInspectionList(action: ApplicationActionType) {
    const licenseId: string = action.payload.id;
    let inspectionCount: number;
    try {
        yield put(licenseActions.getInspectionList.pending);
        const token: string = yield call(getAccessToken, 'inspections.read');
        if (!action.paginationData?.skip) {
            inspectionCount = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getInspectionCount,
                token,
                { 'license.id': licenseId },
            );
        } else {
            inspectionCount = yield select(getInspectionCount);
        }
        const response: Inspection[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getInspections,
            token,
            licenseId,
            action?.paginationData,
        );

        yield put(licenseActions.getInspectionList.success(response, action.paginationData, inspectionCount));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getInspectionList.error(error));
    }
}

function* getCommentList(action: ApplicationActionType) {
    const licenseId: string = action.payload.id;
    try {
        yield put(licenseActions.getCommentList.pending);
        const token: string = yield call(getAccessToken, 'licenses.read');
        const response: CommentList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getCommentList,
            token,
            licenseId,
            action.paginationData,
        );
        yield put(licenseActions.getCommentList.success(response.length > 0 ? response : [], action.paginationData));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getCommentList.error(error));
    }
}

function* getCategoryList(action: { type: string; payload: { licenseTypeId: string } }) {
    try {
        yield put(licenseActions.getCategoryList.pending);
        const { licenseTypeId } = action?.payload;
        const token: string = yield call(getAccessToken, 'violationCategories.read');
        const response: ViolationCategory[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getCategories,
            token,
            licenseTypeId,
        );
        yield put(licenseActions.getCategoryList.success(response));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getCategoryList.error(error));
    }
}

function* addViolation(action: AddViolationType) {
    const data: Violation[] = action.payload.data;
    try {
        yield put(licenseActions.addViolation.pending);
        const token: string = yield call(getAccessToken, 'violations.write');
        const result: Violation[] = [];
        for (let index = 0; index < data.length; index++) {
            const element = data[index];
            const violation: Violation = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.addViolation,
                token,
                element,
            );
            violation.id && result.push(violation);
        }
        const licenseId = result[0].license?.id;
        const paginationData = {
            limit: 20,
            skip: 0,
        };
        // This is to refresh/get the sorted violationList with newly added record.
        yield put(licenseActions.getViolationListBegin(licenseId, paginationData));
        yield put(licenseActions.addViolation.success(result));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Violation has been added.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.addViolation.error(error));
    }
}

function* addInspection(action: AddInspectionActionType) {
    const data: Inspection = action.payload.data;
    try {
        yield put(licenseActions.addInspection.pending);
        const token: string = yield call(getAccessToken, 'inspections.write');
        const result: Inspection = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addInspection,
            token,
            data,
        );
        yield put(licenseActions.addInspection.success(result));
        yield put(licenseActions.getInspectionListBegin(data.license.id, { limit: 20, skip: 0 }));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Inspection has been added.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.addInspection.error(error));
    }
}

function* addComment(action: AddInspectionActionType) {
    const data: any = {
        text: action.payload.data.text,
    };
    const licenseId: string = action.payload.data.licenseId;
    try {
        yield put(licenseActions.addComment.pending);
        const token: string = yield call(getAccessToken, 'licenses.write');
        const result: Comment = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addComment,
            token,
            data,
            licenseId,
        );
        yield put(licenseActions.addComment.success(result));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Comment has been added.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.addComment.error(error));
    }
}

function* deleteInspectionRecord(action: DeleteInspectionActionType) {
    const inspectionId: string = action.payload.inspectionId;
    try {
        yield put(licenseActions.deleteInspectionRecord.pending);
        const token: string = yield call(getAccessToken, 'inspections.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.deleteInspection,
            token,
            inspectionId,
        );
        yield put(licenseActions.deleteInspectionRecord.success(doubleDecodeURL(inspectionId)));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Inspection has been removed.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.deleteInspectionRecord.error(error));
    }
}

function* getUserList() {
    try {
        yield put(licenseActions.getUserList.pending);
        const token: string = yield call(getAccessToken, 'userProfiles.read');
        const response: User[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getUser,
            token,
        );
        yield put(licenseActions.getUserList.success(response));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getUserList.error(error));
    }
}

function* getInspectionType(action: { type: string; payload: { licenseTypeId: string } }) {
    try {
        yield put(licenseActions.getInspectionType.pending);
        const token: string = yield call(getAccessToken, 'inspectionTypes.read');
        const response: InspectionType[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getInspectionType,
            token,
            action?.payload?.licenseTypeId,
        );
        yield put(licenseActions.getInspectionType.success(response));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getInspectionType.error(error));
    }
}

function* deleteViolation(action: DeleteViolationActionType) {
    const violationId: string = action.payload.violationId;
    try {
        yield put(licenseActions.deleteViolation.pending);
        const token: string = yield call(getAccessToken, 'violations.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.deleteViolation,
            token,
            violationId,
        );
        yield put(licenseActions.deleteViolation.success(doubleDecodeURL(violationId)));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Violation has been removed.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.deleteViolation.error(error));
    }
}
function* getInspectionDetail(action: { type: string; inspectionId: string }) {
    try {
        const editForm: Form = {
            id: action?.inspectionId,
            name: 'Edit Inspection',
            sections: editFormData.sections,
        };
        yield put(licenseActions.getInspectionDetail.pending);
        const successData = {
            editForm,
        };
        yield put(licenseActions.getInspectionDetail.success(successData));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getInspectionDetail.error(error));
    }
}

function* addLicense(action: AddLicenseActionType) {
    const license: License = action.payload.license;
    const history: any = action.payload.history;
    try {
        yield put(licenseActions.addLicense.pending);
        const token: string = yield call(getAccessToken, 'licenses.write');
        const result: License = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addLicense,
            token,
            license,
        );
        yield put(licenseActions.addLicense.success(result));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'License has been added.' }));
        yield call(redirectToURL, history, BASE_URL + `/license/${result.id}/detail`);
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.addLicense.error(error));
    }
}

function* getMergeTemplates(action: any) {
    try {
        yield put(licenseActions.getMergeTemplates.pending);
        const token: string = yield call(getAccessToken, 'mergeTemplates.read');
        const result: MergeTemplate = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getMergeTemplates,
            token,
            action.payload,
        );

        yield put(licenseActions.getMergeTemplates.success(result));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getMergeTemplates.error(error));
    }
}

function* mergeDocuments(action: DownloadTemplateDocumentActionType) {
    const templateId = action.payload.templateId;
    const fileName = action.payload.fileName;
    const printHistoryId = action.payload.printHistoryId;
    const records = action.payload.records || [];
    const outputFormat = action.payload.outputFormat;
    try {
        yield put(licenseActions.mergeDocuments.pending);
        const token: string = yield call(getAccessToken, 'mergeTemplates.read');
        const result: { response: string; status: number } = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.mergeDocuments,
            token,
            templateId,
            fileName,
            printHistoryId,
            false,
            [],
            records,
            outputFormat,
        );

        yield put(licenseActions.mergeDocuments.success(result));
        if (result.status === 200) {
            yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: result.response }));
            const payload = {
                templateId,
            };
            // this is to refresh the list on every document download
            yield getPrintDocumentsHistoryList({
                payload,
                type: 'PRINT_DOCUMENTS_HISTORY_LIST',
                data: { limit: 10, skip: 0 },
            });
        } else {
            yield put(siteActions.setError({ isError: true, errorCode: 1, message: result.response }));
        }
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.mergeDocuments.error(error));
    }
}

function* getPrintDocumentsHistoryList(action: PrintDocumentHistoryListActionType) {
    const templateId: string = action?.payload.templateId;
    try {
        yield put(licenseActions.getPrintDocumentsHistoryList.pending);
        const token: string = yield call(getAccessToken, 'mergeTemplates.read');
        const result: MergeQueueHistory[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPrintDocumentHistoryList,
            token,
            templateId,
            action?.data,
        );

        if (result.length) {
            yield put(licenseActions.getPrintDocumentsHistoryList.success(result, action?.data));
        } else {
            yield put(licenseActions.getPrintDocumentsHistoryList.success([], action?.data));
        }
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(licenseActions.getPrintDocumentsHistoryList.error(error));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(ACTIONS.GET_LICENSE_ACCOUNTING_BEGIN, getLicenseAccounting),
        takeEvery(ACTIONS.GET_AUDIT_HISTORY_BEGIN, getAuditHistoryList),
        debounce(500, ACTIONS.LICENSE_SEARCH_BEGIN, licenseSearch),
        takeEvery(ACTIONS.GET_APPLICATIONS_DETAIL_BEGIN, getApplication),
        takeEvery(ACTIONS.GET_APPLICATIONS_LIST_BEGIN, getApplicationList),
        takeLatest(ACTIONS.UPDATE_LICENSE_BEGIN, updateLicense),
        takeLatest(ACTIONS.APPLY_FILTERS, applyFilter),
        takeLatest(ACTIONS.EXPORT_LICENSES, exportLicenses),
        takeLatest(ACTIONS.GET_LICENSE_TYPES_BEGIN, getLicenseTypes),
        takeEvery(ACTIONS.GET_VIOLATION_LIST_BEGIN, getViolationList),
        takeEvery(ACTIONS.GET_INSPECTION_LIST_BEGIN, getInspectionList),
        takeEvery(ACTIONS.GET_COMMENT_LIST_BEGIN, getCommentList),
        takeEvery(ACTIONS.GET_CATEGORY_LIST_BEGIN, getCategoryList),
        takeEvery(ACTIONS.ADD_VIOLATION_BEGIN, addViolation),
        takeEvery(ACTIONS.DELETE_INSPECTION_RECORD_BEGIN, deleteInspectionRecord),
        takeEvery(ACTIONS.ADD_INSPECTION_BEGIN, addInspection),
        takeEvery(ACTIONS.ADD_COMMENT_BEGIN, addComment),
        takeEvery(ACTIONS.GET_USER_LIST_BEGIN, getUserList),
        takeEvery(ACTIONS.GET_INSPECTION_TYPE_BEGIN, getInspectionType),
        takeEvery(ACTIONS.DELETE_VIOLATION_BEGIN, deleteViolation),
        takeEvery(ACTIONS.GET_INSPECTION_DETAIL_BEGIN, getInspectionDetail),
        takeEvery(ACTIONS.ADD_LICENSE_BEGIN, addLicense),
        takeEvery(ACTIONS.GET_MERGE_TEMPLATES_BEGIN, getMergeTemplates),
        takeEvery(ACTIONS.DOWNLOAD_DOCUMENT_BEGIN, mergeDocuments),
        takeEvery(ACTIONS.PRINT_DOCUMENTS_HISTORY_LIST_BEGIN, getPrintDocumentsHistoryList),
        takeEvery(ACTIONS.DELETE_LICENSE_FILE, deleteLicenseFile),
    ]);
}
